import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { savingsService } from '../../services/savingsService';
import { memberService } from '../../services/memberService';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatters';

const SavingsForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const memberIdFromUrl = new URLSearchParams(location.search).get('memberId');

  const [selectedMember, setSelectedMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [formData, setFormData] = useState({
    member_id: memberIdFromUrl || '',
    scheme_id: '',
    deposit_amount: '',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [calculatedMaturity, setCalculatedMaturity] = useState(null);

  // Fetch deposit schemes
  const { data: schemesData, isLoading: schemesLoading } = useQuery(
    'deposit-schemes',
    () => savingsService.getDepositSchemes()
  );

  const schemes = schemesData?.data || [];

  // Search members
  const searchMembers = async () => {
    if (searchTerm.length >= 2) {
      setIsSearching(true);
      try {
        const result = await memberService.searchMembers(searchTerm);
        setSearchResults(result.data || []);
      } catch (error) {
        console.error('Search error:', error);
        toast.error('Failed to search members');
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  // If memberId is provided in URL, load member details
  useEffect(() => {
    if (memberIdFromUrl) {
      loadMemberDetails(memberIdFromUrl);
    }
  }, [memberIdFromUrl]);

  const loadMemberDetails = async (memberId) => {
    try {
      const result = await memberService.getMemberById(memberId);
      setSelectedMember(result.data);
      setFormData(prev => ({ ...prev, member_id: memberId }));
    } catch (error) {
      console.error('Error loading member:', error);
      toast.error('Failed to load member details');
    }
  };

  // Calculate maturity when scheme or amount changes
  useEffect(() => {
    if (selectedScheme && formData.deposit_amount) {
      const scheme = schemes.find(s => s.id === parseInt(selectedScheme));
      if (scheme && scheme.duration_months) {
        const principal = parseFloat(formData.deposit_amount);
        const rate = scheme.interest_rate;
        const months = scheme.duration_months;

        // Simple maturity calculation
        let maturity;
        if (scheme.interest_type === 'flat') {
          const interest = principal * (rate / 100) * (months / 12);
          maturity = principal + interest;
        } else {
          const monthlyRate = rate / 100 / 12;
          maturity = principal * Math.pow(1 + monthlyRate, months);
        }

        setCalculatedMaturity({
          amount: maturity.toFixed(2),
          interest: (maturity - principal).toFixed(2),
          date: new Date(Date.now() + months * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
      } else {
        setCalculatedMaturity(null);
      }
    }
  }, [selectedScheme, formData.deposit_amount, schemes]);

  const handleSearch = () => {
    if (searchTerm.length >= 2) {
      searchMembers();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const selectMember = (member) => {
    setSelectedMember(member);
    setFormData({ ...formData, member_id: member.id });
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleSchemeChange = (e) => {
    const schemeId = e.target.value;
    setSelectedScheme(schemeId);
    setFormData({ ...formData, scheme_id: schemeId });
  };

  const createAccountMutation = useMutation(
    (data) => savingsService.createAccount(data),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['member-savings', formData.member_id]);
        toast.success(response.message || 'Savings account created successfully!');
        navigate('/savings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to create account');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.member_id) {
      toast.error('Please select a member');
      return;
    }

    if (!formData.scheme_id) {
      toast.error('Please select a deposit scheme');
      return;
    }

    if (!formData.deposit_amount || parseFloat(formData.deposit_amount) <= 0) {
      toast.error('Please enter a valid deposit amount');
      return;
    }

    const selectedSchemeObj = schemes.find(s => s.id === parseInt(formData.scheme_id));
    if (selectedSchemeObj) {
      if (parseFloat(formData.deposit_amount) < selectedSchemeObj.minimum_deposit) {
        toast.error(`Minimum deposit amount is ${formatCurrency(selectedSchemeObj.minimum_deposit)}`);
        return;
      }
      if (selectedSchemeObj.maximum_deposit && parseFloat(formData.deposit_amount) > selectedSchemeObj.maximum_deposit) {
        toast.error(`Maximum deposit amount is ${formatCurrency(selectedSchemeObj.maximum_deposit)}`);
        return;
      }
    }

    createAccountMutation.mutate({
      member_id: parseInt(formData.member_id),
      scheme_id: parseInt(formData.scheme_id),
      deposit_amount: parseFloat(formData.deposit_amount),
      start_date: formData.start_date
    });
  };

  const selectedSchemeDetails = schemes.find(s => s.id === parseInt(selectedScheme));

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Open Savings Account</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Member Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">1. Select Member</h2>

          {!selectedMember ? (
            <div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, phone, or member code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  className="absolute right-3 top-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Search
                </button>
              </div>

              {isSearching && <div className="mt-2 text-gray-500">Searching...</div>}

              {searchResults.length > 0 && (
                <div className="mt-4 border rounded-lg max-h-60 overflow-y-auto">
                  {searchResults.map(member => (
                    <div
                      key={member.id}
                      onClick={() => selectMember(member)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                    >
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-gray-500">
                        Code: {member.member_code} | Phone: {member.phone}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div>
                <div className="font-semibold text-lg">{selectedMember.name}</div>
                <div className="text-sm text-gray-600">
                  Code: {selectedMember.member_code} | Phone: {selectedMember.phone}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedMember(null);
                  setFormData({ ...formData, member_id: '' });
                }}
                className="text-red-600 hover:text-red-800"
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Scheme Selection */}
        {selectedMember && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">2. Select Deposit Scheme</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Deposit Scheme *</label>
                <select
                  value={selectedScheme}
                  onChange={handleSchemeChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={schemesLoading}
                >
                  <option value="">Select Scheme</option>
                  {schemes.map(scheme => (
                    <option key={scheme.id} value={scheme.id}>
                      {scheme.scheme_name} - {scheme.interest_rate}%
                      ({scheme.scheme_type.replace('_', ' ').toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Deposit Amount *</label>
                <input
                  type="number"
                  value={formData.deposit_amount}
                  onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Scheme Details */}
            {selectedSchemeDetails && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-2">Scheme Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-gray-600">Interest Rate</div>
                    <div className="font-medium">{selectedSchemeDetails.interest_rate}%</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Interest Type</div>
                    <div className="font-medium capitalize">{selectedSchemeDetails.interest_type}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Min Deposit</div>
                    <div className="font-medium">{formatCurrency(selectedSchemeDetails.minimum_deposit)}</div>
                  </div>
                  <div>
                    <div className="text-gray-600">Duration</div>
                    <div className="font-medium">{selectedSchemeDetails.duration_months || 'Flexible'} months</div>
                  </div>
                </div>
              </div>
            )}

            {/* Maturity Calculation */}
            {calculatedMaturity && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold mb-2">Projected Maturity</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-gray-600">Maturity Amount</div>
                    <div className="text-xl font-bold text-green-600">{formatCurrency(parseFloat(calculatedMaturity.amount))}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Interest</div>
                    <div className="text-xl font-bold text-blue-600">{formatCurrency(parseFloat(calculatedMaturity.interest))}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Maturity Date</div>
                    <div className="text-xl font-bold text-orange-600">{calculatedMaturity.date}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        {selectedMember && (
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/savings')}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createAccountMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createAccountMutation.isLoading ? 'Creating...' : 'Open Account'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default SavingsForm;